// ***************************************************************************************
// Sharing a result.
//
// THE SHARE IS A PICTURE, NOT A SENTENCE. What made Wordle spread was not the game, it
// was a spoiler-free little image of emoji that people could paste anywhere and that
// looked like something. A line of prose saying "148 points, longest word 7" is a fact
// nobody reposts.
//
// The equivalent here is the WORDS THEMSELVES AS SHAPES: one row per word, as wide as
// the word is long, coloured by how unusual it was. A seven-wide row is a brag you can
// see from across the room, and the ragged right edge is different for every game, which
// is the part that makes two people compare.
//
// IT STILL CONTAINS NO WORDS. That rule has not changed and is why this is safe to post
// while the puzzle is live: lengths and rarity, never letters.
// ***************************************************************************************

import { TIER_COOL, TIER_NICE } from '../engine/rules.ts';

/** How many word rows a share may have before it stops being a glance. */
const MAX_ROWS = 6;

// The tiers, in the game's own colours: ordinary words green, Nice yellow, Cool blue -
// matching the yellow and cyan the board itself uses for them.
const ORDINARY = '🟩';
const NICE = '🟨';
const COOL = '🟦';
/** A tile still on the board, or a challenge not taken. */
const EMPTY = '⬜';
const CLAIMED = '⭐';

export interface ShareWord {
  readonly length: number;
  readonly tier: number;
}

export interface ShareInput {
  /** How the mode names itself in a shared result, e.g. "Word Soup" or "Word Soup Hunt". */
  title: string;
  displayNumber: number;
  score: number;
  /** Every word played, in play order. Lengths and tiers only - never the words. */
  words: readonly ShareWord[];
  cleared: boolean;
  tilesRemaining: number;
  puzzleId: string;
  /** Days in a row. 0 when there is no streak to speak of. */
  streak: number;
  /** Percent of today's other players beaten, or null when there is nobody to compare. */
  beatPercent: number | null;
  /** Ladder: how many rungs of how many. */
  rungs?: { done: number; total: number };
  /** Hunt: how many challenges taken of how many. */
  achievements?: { claimed: number; total: number };
}

export type ShareOutcome =
  | { kind: 'shared' }
  | { kind: 'copied' }
  | { kind: 'failed'; reason: string };

export function shareUrl(puzzleId: string): string {
  // A puzzle-specific link, not "today". Somebody opening this tomorrow gets the puzzle
  // that was shared, not whatever is current.
  return `${location.origin}/?puzzle=${encodeURIComponent(puzzleId)}`;
}

function square(tier: number): string {
  if (tier === TIER_COOL) return COOL;
  if (tier === TIER_NICE) return NICE;

  return ORDINARY;
}

/**
 * The grid: the best few words as rows of squares, longest first.
 *
 * Longest first rather than in play order, because the shape is the point and a
 * descending staircase reads as an achievement where the same rows shuffled read as a
 * list. Ties break towards the rarer word, so the blue row wins the top of the tie.
 */
export function shareGrid(words: readonly ShareWord[]): string[] {
  const ranked = words
    .slice()
    .sort((a, b) => b.length - a.length || b.tier - a.tier)
    .slice(0, MAX_ROWS);

  return ranked.map((word) => square(word.tier).repeat(word.length));
}

function progress(done: number, total: number, marker: string): string {
  const taken = Math.max(0, Math.min(total, done));

  return marker.repeat(taken) + EMPTY.repeat(total - taken);
}

export function shareText(input: ShareInput, url: string = shareUrl(input.puzzleId)): string {
  const lines: string[] = [`${input.title} #${input.displayNumber} — ${input.score}`];

  lines.push(...shareGrid(input.words));

  const hidden = input.words.length - MAX_ROWS;

  if (hidden > 0) lines.push(`+${hidden} more word${hidden === 1 ? '' : 's'}`);

  // The mode's own scoreboard, when it has one. These read at a glance and are the part
  // a player of that mode compares first.
  if (input.achievements && input.achievements.total > 0) {
    lines.push(
      `${progress(input.achievements.claimed, input.achievements.total, CLAIMED)} ` +
        `${input.achievements.claimed} of ${input.achievements.total} challenges`,
    );
  }

  if (input.rungs && input.rungs.total > 0) {
    lines.push(
      `${progress(input.rungs.done, input.rungs.total, ORDINARY)} ` +
        `${input.rungs.done} of ${input.rungs.total} rungs`,
    );
  }

  // One line of facts, only for the facts worth having. An empty streak and a percentile
  // computed from nobody are both worse than silence.
  const facts: string[] = [];

  if (input.cleared) facts.push('cleared the board');
  else facts.push(`${input.tilesRemaining} tile${input.tilesRemaining === 1 ? '' : 's'} left`);

  if (input.beatPercent !== null) facts.push(`beat ${input.beatPercent}% today`);
  if (input.streak > 1) facts.push(`🔥 ${input.streak} days`);

  lines.push(facts.join(' · '));
  lines.push(url);

  return lines.join('\n');
}

export async function shareResult(input: ShareInput): Promise<ShareOutcome> {
  const text = shareText(input);

  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ text });

      return { kind: 'shared' };
    } catch (thrown: unknown) {
      // A cancelled share is not a failure and must not fall through to the clipboard -
      // silently copying something the player just decided not to share is worse than
      // doing nothing.
      if (thrown instanceof DOMException && thrown.name === 'AbortError') {
        return { kind: 'failed', reason: '' };
      }

      // Anything else: fall through and try the clipboard.
    }
  }

  try {
    await navigator.clipboard.writeText(text);

    return { kind: 'copied' };
  } catch (thrown: unknown) {
    // Say what happened. "Copy failed" with no reason leaves the player unable to tell a
    // permissions prompt from a broken button.
    const reason =
      thrown instanceof Error && thrown.name === 'NotAllowedError'
        ? 'your browser blocked clipboard access'
        : 'your browser would not copy it';

    return { kind: 'failed', reason };
  }
}
